/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ITelemetry, ITelemetryModuleContext, LogLevel, NumericLogLevel } from '../interfaces';
import { TelemetryBase } from './telemetry-base';
import { TelemetryLogOptions } from './telemetry-log-options';

/**
 * Telemetry client containing trace loggers
 */
export class Telemetry extends TelemetryBase implements ITelemetry {
    /**
     * Creates a new instance of Telemetry.
     */
    public constructor() {
        super();
    }

    /**
     * Logs a trace message with the Trace log level
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    public trace = (message: string, ...args: unknown[]): void => {
        this._logTrace(LogLevel.Trace, message, args);
    };

    /**
     * Logs a trace message with the Debug log level
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    public debug = (message: string, ...args: unknown[]): void => {
        this._logTrace(LogLevel.Debug, message, args);
    };

    /**
     * Logs a trace message with the Information log level
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    public information = (message: string, ...args: unknown[]): void => {
        this._logTrace(LogLevel.Information, message, args);
    };

    /**
     * Logs a trace message with the Warning log level
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    public warning = (message: string, ...args: unknown[]): void => {
        this._logTrace(LogLevel.Warning, message, args);
    };

    /**
     * Logs a trace message with the Error log level
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    public error = (message: string, ...args: unknown[]): void => {
        this._logTrace(LogLevel.Error, message, args);
    };

    /**
     * Logs a trace message with the Critical log level
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    public critical = (message: string, ...args: unknown[]): void => {
        this._logTrace(LogLevel.Critical, message, args);
    };

    /**
     * Logs an Error object as an exception
     * @param error The Error object to log
     */
    public exception = (error: Error): void => {
        this.notifyTraceLoggers(logger => logger.exception(error, this._telemetryContext));
    };

    /**
     * Sets the properties specific to the current module that should be added to every log
     * @param moduleContext Contains the context items specific to the current module
     */
    public setTelemetryModuleContext = (moduleContext: ITelemetryModuleContext) => {
        return () => {
            // Create copy of telemetry object with specified module context
            // TODO: kopik do we need to create new instance of telemetry here or can we just capture in closure?
            const telemetryWithContext = new Telemetry();
            telemetryWithContext._enableDataAnalytics = this._enableDataAnalytics;
            telemetryWithContext._webAnalyticsPlugin = this._webAnalyticsPlugin;
            telemetryWithContext._traceLoggers = this._traceLoggers;
            telemetryWithContext._eventLoggers = this._eventLoggers;
            telemetryWithContext._expLogger = this._expLogger;
            telemetryWithContext._attributeRenderers = this._attributeRenderers;
            telemetryWithContext._telemetryContext.requestContext = this._telemetryContext.requestContext;
            telemetryWithContext._telemetryContext.moduleContext = moduleContext;

            return telemetryWithContext;
        };
    };

    /**
     * Logs a structured message at the given log level, with optional custom tags and/or an exception
     * @param logLevel LogLevel of the message
     * @param messageTemplate String to log, denoting placeholders with {placeholder}.
     * Example input string: "Hello {signedInUser}! Good {morningOrEvening}!"
     * @param logOptions Contains arguments to replace placeholders, custom tags for log, and optional error object.
     * For the example message template, arguments need to be provided for 'user' and 'morningOrEvening'.
     */
    public log = (logLevel: LogLevel, messageTemplate: string, logOptions?: TelemetryLogOptions): void => {
        this._log(logLevel, messageTemplate, logOptions);
    };

    /**
     * Logs a trace message with the given LogLevel
     * @param logLevel LogLevel of the trace message
     * @param message The string to log
     * @param args Any extra arguments to log. Will be converted to strings and appended to message
     */
    private _logTrace = (logLevel: LogLevel, message: string, args: unknown[]): void => {
        const requestedLogLevel = NumericLogLevel[logLevel];
        const minLogLevel = <number>(<unknown>process.env.SDK_MIN_LOG_LEVEL);
        if (minLogLevel <= requestedLogLevel) {
            this.notifyTraceLoggers(logger => logger.log(logLevel, message, args, this._telemetryContext));
        }
    };
}
